import {
	EVENT_BUILDING_UNLOCK_LEVEL,
	OFFER_BUILDING_UNLOCK_LEVEL,
	SAFE_COORDINATE_OUTSIDE_MAP,
} from "@/config/constants";
import { isTemporaryEventBuildingActive, useEventStore } from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import { getRoadConfig, isRoad } from "@/game/entities/config/roads.ts";
import { UpgradeIds } from "@/game/entities/config/upgrades";
import useEmitter from "@/hooks/useEmitter";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import useResourcesStore from "@/store/useResourcesStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import { type Timestamp } from "@/types";
import {
	type ProductionBase,
	type ProductionDefaultData,
	type ProductionValue,
	type ResourceValue,
	type Reward,
	type SuppliesValue,
	TileAction,
	type TileConfigItem,
	type TileCoordinates,
	TileEntityParam,
	type TileEntityParams,
	type TileIndex,
	type TileNeighbour,
	type UpgradeParams,
	type UpgradeRewardValue,
} from "@/types/tile";
import { floorNumber } from "@/utils/number";
import { canTranslate, translate } from "@/utils/translate";
import type TileRepository from "../TileRepository";
import { type TileRepositoryLayerIndex } from "../TileRepository";
import type SettlementCastleBuildingTileEntity from "./buildings/SettlementCastleBuildingTileEntity";
import {
	FrameName,
	tilerepoBanditsFrameNames,
	tilerepoBigFishingBoatsFrameNames,
	tilerepoBlastFurnacesFrameNames,
	tilerepoBuildingsFrameNames,
	tilerepoFarmFieldsFrameNames,
	tilerepoFightableEnemiesFrameNames,
	tilerepoFightableMainEnemiesFrameNames,
	tilerepoFishingBoatsFrameNames,
	tilerepoFishTrapsFrameNames,
	tilerepoFriendlyUnitsFrameNames,
	tilerepoLayer0FrameNames,
	tilerepoLayer1FrameNames,
	tilerepoLayer2FrameNames,
	tilerepoLayer3FrameNames,
	tilerepoLightHousesFrameNames,
	tilerepoLookoutTowersFrameNames,
	tilerepoNonSellableFrameNames,
	tilerepoNotStorableFrameNames,
	tilerepoPiratesFrameNames,
	tilerepoPremiumBuildings,
	tilerepoPurchasableBuildingsTupleArrays,
	tilerepoPurchaseableEventBuildings,
	tilerepoRemovableEventBuildingsFrameNames,
	tilerepoRoadsFrameNames,
	tilerepoSettlementBuildingsFrameNames,
	tilerepoSettlementCastleBuildingsFrameNames,
	tilerepoSettlementEmptyFrameNames,
} from "./config";
import { getBuildingConfig, isBuilding } from "./config/buildings";
import { getPoiConfig, isPoi } from "./config/pois";
import { getSalvageableConfig, isSalvageable } from "./config/salvageables";
import {
	ALL_VISIBILITY_TYPES,
	CUTTABLE_LEVEL,
	getTerrainConfig,
	isTerrain,
	TileVisibilityType,
} from "./config/terrain";
import type AbstractPoiTileEntity from "./poi/AbstractPoiTileEntity";

export interface TileEntityWithOnBuildMethod {
	onBuild(): void;
}

export interface TileEntityWithOnDestroyMethod {
	onDestroy(): void;
}

export interface TileEntityWithOnClickMethod {
	onClick(): void;
}

export interface TileEntityWithOnRevealMethod {
	onReveal(): void;
}

export interface TileEntityWithOnActivateMethod {
	onActivate(): void;
}

export interface TileEntityWithOnTickMethod {
	onTick(): void;
}

export interface TileEntityWithOnUpgradeMethod {
	onUpgrade(upgradedTile: TileCoordinates): void;
}

const EMPTY_PRODUCTION: Map<ResourceType, number> = new Map<
	ResourceType,
	number
>();

class TileEntity {
	public readonly index: TileIndex;

	public readonly x: TileCoordinates["x"];

	public readonly y: TileCoordinates["y"];

	public readonly tilerepo: TileRepository;

	public static readonly frameName: FrameName = FrameName.NOT_SET;

	// TODO: Toto nemusi byt static, prosim prerobit.
	public static readonly baseGemsPrice: number = 0;

	public exploredTimestamp: number | null = null;

	public showProductionInfo: boolean = false;

	public showConsumptionInfo: boolean = false;

	public isConsumptionSatisfied: boolean = true;

	public buildingLimit: number | null = null;

	protected readonly emitter: ReturnType<typeof useEmitter>;

	protected cachedProduction: Map<ResourceType, number> | null = null;

	protected cachedUpgrades: Map<FrameName, UpgradeParams> | null = null;

	protected cachedCanUpgradeTo: Set<FrameName> | null = null;

	// TODO: move to tilerepo
	// Set this after initial render.
	public static isMounted: boolean = false;

	public readonly createdAt: Timestamp = Date.now();

	public constructor({ x, y }: TileCoordinates, tilerepo: TileRepository) {
		this.emitter = useEmitter();

		this.tilerepo = tilerepo;

		this.index = this.tilerepo.getTileIndexByTileCoordinates({ x, y });

		this.x = x;

		this.y = y;

		if (this.constructor.name === "TileEntity")
			throw new Error("Cannot instantiate TileEntity directly!");
	}

	public get frameName(): FrameName {
		// Returns static property.
		return (this.constructor as typeof TileEntity).frameName;
	}

	public get baseGemsPrice(): number {
		// Returns static property.
		return (this.constructor as typeof TileEntity).baseGemsPrice;
	}

	public get layerIndex(): TileRepositoryLayerIndex {
		if (tilerepoLayer0FrameNames.has(this.frameName)) return 0;
		if (tilerepoLayer1FrameNames.has(this.frameName)) return 1;
		if (tilerepoLayer2FrameNames.has(this.frameName)) return 2;
		if (tilerepoLayer3FrameNames.has(this.frameName)) return 3;
		// if (tilerepoLayer4FrameNames.has(this.frameName)) return 4;

		throw new Error(`Invalid layer index for "${this.frameName}"!`);
	}

	public get name(): string {
		const key = `${this.frameName}.name`;
		if (!canTranslate(key)) return "";
		return translate(key);
	}

	public get description(): string {
		const key = `${this.frameName}.description`;
		if (!canTranslate(key)) return "";
		return translate(key);
	}

	public get action(): TileAction | null {
		return this.config?.action ?? null;
	}

	// TODO: Toto nestaci len v POI entite?
	public get rewardText(): string[] {
		return this.config?.rewardText ?? [];
	}

	// TODO: Toto nestaci len v POI entite?
	public get rewardTextReplace(): { [key: string]: string }[] {
		return this.config?.rewardTextReplace ?? [];
	}

	public static getEntityConfigByFrameName(
		frameName: FrameName,
	): TileConfigItem {
		if (isTerrain(frameName)) return getTerrainConfig(frameName);
		if (isPoi(frameName)) return getPoiConfig(frameName);
		if (isBuilding(frameName)) return getBuildingConfig(frameName);
		if (isSalvageable(frameName)) return getSalvageableConfig(frameName);
		if (isRoad(frameName)) return getRoadConfig(frameName);

		throw new Error(`Invalid frame name: "${frameName}"!`);
	}

	protected get config(): TileConfigItem {
		return (
			this.constructor as typeof TileEntity
		).getEntityConfigByFrameName(this.frameName);
	}

	public get isVisible(): boolean | null {
		return this.config?.isVisible ?? null;
	}

	public get isConnectedToMainSettlement(): boolean {
		return (
			this.getParamByName(
				TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
			) === 1
		);
	}

	// Toto je lepsia metoda, ako to vyssie, ktora aj pozera entitu, ci musi mat cestu.
	public get isConnectionToMainSettlementActive(): boolean {
		const isActive =
			!this.requirements.has(ResourceType.SETTLEMENT_CONNECTION) ||
			(this.requirements.has(ResourceType.SETTLEMENT_CONNECTION) &&
				this.requirements.get(ResourceType.SETTLEMENT_CONNECTION) ===
					0) ||
			(this.requirements.has(ResourceType.SETTLEMENT_CONNECTION) &&
				this.requirements.get(ResourceType.SETTLEMENT_CONNECTION) ===
					1 &&
				this.isConnectedToMainSettlement);
		return isActive;
	}

	public get isExplored(): boolean {
		return (
			this.getParamByName(TileEntityParam.VISIBILITY) ===
			TileVisibilityType.EXPLORED
		);
	}

	public get isBlockedByEnemy(): boolean {
		return this.getParamByName(TileEntityParam.IS_BLOCKED_BY_ENEMY) === 1;
	}

	public get isBlockedBy(): "bandits" | "pirates" | "enemies" | null {
		if (!this.isBlockedByEnemy) return null;

		// TODO: dynamically from config
		const MAX_ENEMY_RANGE = 4;

		return (
			this.tilerepo
				.getNeighbourTileIndexesByTileCoordinates(
					{ x: this.x, y: this.y },
					MAX_ENEMY_RANGE,
					false,
					ALL_VISIBILITY_TYPES,
				)
				.map(
					(
						tileIndex: TileIndex,
					): "bandits" | "pirates" | "enemies" | null => {
						const [, , tileId] =
							this.tilerepo.getTileIdsByTileIndex(tileIndex);
						if (!tileId) return null;

						const frameName =
							this.tilerepo.getFrameNameByTileId(tileId);
						if (!frameName) return null;

						if (tilerepoPiratesFrameNames.has(frameName))
							return "pirates";
						if (tilerepoBanditsFrameNames.has(frameName))
							return "bandits";
						if (
							tilerepoFightableMainEnemiesFrameNames.has(
								frameName,
							)
						)
							return "enemies";
						return null;
					},
				)
				.find(
					(
						value: "bandits" | "pirates" | "enemies" | null,
					): boolean => value !== null,
				) || null
		);
	}

	public get exploreCoefficient(): number | null {
		return this.config?.exploreCoefficient
			? this.config?.exploreCoefficient * (this.isBlockedByEnemy ? 3 : 1)
			: null;
	}

	public get range(): number | null {
		if (!this.config?.range) return null;

		return this.config.range;
	}

	public get siegeAttributes(): TileConfigItem["siegeAttributes"] | null {
		return this.config.siegeAttributes ?? null;
	}

	public get requirements(): Map<ResourceType, number> {
		return this.config.requirements;
	}

	// public get hasOnlyStaticProduction(): boolean {
	// 	for (const value of this.config.production.values()) {
	// 		if (typeof value === "function") {
	// 			return false;
	// 		}
	// 	}

	// 	return true;
	// }

	private get isFakeEntity(): boolean {
		return (
			this.x === SAFE_COORDINATE_OUTSIDE_MAP &&
			this.y === SAFE_COORDINATE_OUTSIDE_MAP
		);
	}

	protected get canHaveProduction(): boolean {
		return (
			this.isFakeEntity ||
			this.showProductionInfo ||
			((!this.requirements.has(ResourceType.SETTLEMENT_CONNECTION) ||
				(this.requirements.has(ResourceType.SETTLEMENT_CONNECTION) &&
					this.requirements.get(
						ResourceType.SETTLEMENT_CONNECTION,
					) === 0) ||
				(this.requirements.has(ResourceType.SETTLEMENT_CONNECTION) &&
					this.requirements.get(
						ResourceType.SETTLEMENT_CONNECTION,
					) === 1 &&
					this.isConnectedToMainSettlement)) &&
				this.isConsumptionSatisfied)
		);
	}

	// Kazda entita je zodpovedna za to, aby sa vratilo cislo a nie callback.
	public get production(): Map<ResourceType, number> {
		if (!this.canHaveProduction) return EMPTY_PRODUCTION;
		if (this.cachedProduction) return this.cachedProduction;

		const entries = Array.from(this.config.production.entries());
		if (
			entries.every(
				([, value]: [ResourceType, ProductionValue]): boolean =>
					typeof value === "number",
			)
		) {
			// Vytvorime kopiu produkcie, ak by sa niekto ju rozhodol modifikovat, nech cez referenciu nemodifikuje config.
			return (this.cachedProduction = new Map<ResourceType, number>(
				this.config.production as Map<ResourceType, number>,
			));
		}

		return (this.cachedProduction = new Map<ResourceType, number>(
			entries.map(
				([resource, valueOrFunction]: [
					ResourceType,
					ProductionValue,
				]): [ResourceType, number] => [
					resource,
					typeof valueOrFunction === "function"
						? valueOrFunction(this)
						: valueOrFunction,
				],
			),
		));
	}

	public get productionBase(): ProductionBase | null {
		return (this.config?.productionBase as ProductionBase) ?? null;
	}

	public get productionDefaultData(): ProductionDefaultData[] | null {
		const base = this.productionBase;
		if (!base) return null;

		const out: ProductionDefaultData[] = [];
		for (const [resource] of this.config.production) {
			const b = base[resource];
			if (!b) continue;
			out.push({
				number: b.value, // config base value, see buildings.ts
				icon: resource,
				label: b.frameName, // base unit label, as FrameName.name
			});
		}

		// IMPORTANT: this check ensures that null is returned if productionBase is not filled (*{})
		// NOTE: display base production on building selection screen if productionBase is filled, or calculate it dynamically if it's not filled
		return out.length ? out : null;
	}

	public get consumption(): Map<ResourceType, number> {
		return this.config.consumption;
	}

	public get storage(): Map<ResourceType, number> {
		return this.config.storage;
	}

	public get upgrades(): Map<FrameName, UpgradeParams> {
		if (this.cachedUpgrades) return this.cachedUpgrades;
		return (this.cachedUpgrades = new Map<FrameName, UpgradeParams>(
			this.config.upgrades,
		));
	}

	public get assignedCastleTileIndex(): TileIndex | null {
		if (
			!tilerepoSettlementBuildingsFrameNames.has(this.frameName) &&
			!tilerepoSettlementEmptyFrameNames.has(this.frameName)
		)
			return null;

		const neighbours = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				1,
				false,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				const [, , tileId] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId);
				if (!frameName) return null;

				return {
					tileIndex,
					tileId,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null &&
					tilerepoSettlementCastleBuildingsFrameNames.has(
						neighbour.frameName,
					),
			) as Array<TileNeighbour>;
		if (!neighbours.length) return null;

		return neighbours[0].tileIndex;
	}

	public get assignedCastleTileEntity(): SettlementCastleBuildingTileEntity | null {
		const { assignedCastleTileIndex: tileIndex } = this;
		if (!tileIndex) return null;

		const entity = this.tilerepo.getTileEntityFactoriesByTileIndex(
			tileIndex,
		)[2]() as SettlementCastleBuildingTileEntity | null;
		if (!entity) return null;

		return entity;
	}

	public get supplies(): Set<FrameName> {
		return this.resolveSupplies(
			this.config.supplies ?? new Set<FrameName>(),
		);
	}

	public get suppliesTileIndexes(): Set<TileIndex> {
		const tileIndexes: Set<TileIndex> = new Set<TileIndex>();

		const { supplies, range } = this;
		if (!supplies.size) return tileIndexes;

		if (range) {
			const neighbours = this.tilerepo
				.getNeighbourTileIndexesByTileCoordinates(
					{ x: this.x, y: this.y },
					range,
				)
				.map((tileIndex: TileIndex): TileNeighbour | null => {
					const [tileId0, , tileId2] =
						this.tilerepo.getTileIdsByTileIndex(tileIndex);
					const tileId = tileId2 || tileId0;
					if (!tileId) return null;

					const frameName =
						this.tilerepo.getFrameNameByTileId(tileId);
					if (!frameName) return null;

					return {
						tileIndex,
						tileId,
						frameName,
					};
				})
				.filter(
					(neighbour: TileNeighbour | null): boolean =>
						neighbour !== null && supplies.has(neighbour.frameName),
				) as Array<TileNeighbour>;

			for (const { tileIndex } of neighbours) {
				tileIndexes.add(tileIndex);
			}
		}

		for (const frameName of supplies) {
			const targetTileId = this.tilerepo.getTileIdByFrameName(frameName);
			if (!targetTileId) continue;

			const isCastle =
				tilerepoSettlementCastleBuildingsFrameNames.has(frameName);
			const { range } = isCastle
				? { range: 1 } // Hrad musi byt hard-kodnuty, lebo on sa paruje na suradnice settlementu zo storka a tu to kape, ak tam ma SAFE_COORDINATE_OUTSIDE_MAP.
				: (this.tilerepo.createEntityByFrameName(frameName, {
						x: SAFE_COORDINATE_OUTSIDE_MAP,
						y: SAFE_COORDINATE_OUTSIDE_MAP,
					})! ?? {
						range: null,
					});
			if (!range) continue;

			const neighbours = this.tilerepo
				.getNeighbourTileIndexesByTileCoordinates(
					{ x: this.x, y: this.y },
					range,
				)
				.map(
					(
						tileIndex: TileIndex,
					): Omit<TileNeighbour, "frameName"> | null => {
						const [tileId0, , tileId2] =
							this.tilerepo.getTileIdsByTileIndex(tileIndex);
						const tileId = tileId2 || tileId0;
						if (!tileId) return null;

						return {
							tileIndex,
							tileId,
						};
					},
				)
				.filter(
					(
						neighbour: Omit<TileNeighbour, "frameName"> | null,
					): boolean =>
						neighbour !== null && neighbour.tileId === targetTileId,
				) as Array<TileNeighbour>;

			for (const { tileIndex } of neighbours) {
				tileIndexes.add(tileIndex);
			}
		}

		return tileIndexes;
	}

	public get roadCaps(): {
		[FrameName.GRAVEL_ROAD]: number;
		[FrameName.COBBLED_ROAD]: number;
	} | null {
		if (!this.config?.roadCaps) return null;

		return this.config.roadCaps!;
	}

	public get notification(): number | null {
		if (
			!tilerepoNonSellableFrameNames.has(this.frameName) &&
			useUpgradesStore().getCurrentLevelById(UpgradeIds.inventory) <= 0
		)
			return null;

		const buildingsStorage = useBuildingsStorageStore();
		if (tilerepoFishingBoatsFrameNames.has(this.frameName))
			return (
				(buildingsStorage?.[FrameName.SHALLOW_SEA_FISHING_BOAT] ?? 0) +
				(buildingsStorage?.[FrameName.DEEP_SEA_FISHING_BOAT] ?? 0)
			);
		if (tilerepoBigFishingBoatsFrameNames.has(this.frameName))
			return (
				(buildingsStorage?.[FrameName.SHALLOW_SEA_BIG_FISHING_BOAT] ??
					0) +
				(buildingsStorage?.[FrameName.DEEP_SEA_BIG_FISHING_BOAT] ?? 0)
			);
		if (tilerepoFishTrapsFrameNames.has(this.frameName))
			return (
				(buildingsStorage?.[FrameName.SHALLOW_SEA_SMALL_FISH_TRAP] ??
					0) +
				(buildingsStorage?.[FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP] ??
					0)
			);
		if (tilerepoLightHousesFrameNames.has(this.frameName)) {
			return (
				(buildingsStorage?.[FrameName.LIGHTHOUSE] ?? 0) +
				(buildingsStorage?.[FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE] ??
					0)
			);
		}
		if (tilerepoBlastFurnacesFrameNames.has(this.frameName)) {
			return (
				(buildingsStorage?.[FrameName.HILLS_BLAST_FURNACE] ?? 0) +
				(buildingsStorage?.[FrameName.MOUNTAINS_BLAST_FURNACE] ?? 0)
			);
		}
		if (tilerepoLookoutTowersFrameNames.has(this.frameName)) {
			return (
				(buildingsStorage?.[FrameName.HILLS_LOOKOUT_TOWER] ?? 0) +
				(buildingsStorage?.[FrameName.MOUNTAINS_LOOKOUT_TOWER] ?? 0)
			);
		}
		return buildingsStorage?.[this.frameName] ?? null;
	}

	public get isFarmField(): boolean {
		return tilerepoFarmFieldsFrameNames.has(this.frameName);
	}

	public get isDestroyable(): boolean {
		return this.upgrades.has(FrameName.NOT_SET);
	}

	public get isCuttable(): boolean {
		return this.action === TileAction.CUT;
	}

	public get isRemovable(): boolean {
		return this.action === TileAction.REMOVE;
	}

	public get isDefeatable(): boolean {
		return this.isDestroyable && this.action === TileAction.DEFEAT;
	}

	public get rewardRange(): Map<ResourceType, { min: number; max: number }> {
		return this.config.rewardRange ?? new Map();
	}

	public get skipDefeatGemsPrice(): number | null {
		if (!this.isDefeatable) return null;

		const { mainLevel } = useSettlementsStore();
		const armyUnlockLevel =
			getBuildingConfig(FrameName.BARRACKS).requirements.get(
				ResourceType.LEVEL,
			) ?? 0;
		if (mainLevel >= armyUnlockLevel) return null;

		let price =
			this.upgrades.get(FrameName.NOT_SET)?.skipPrice?.[
				ResourceType.GEMS
			] ?? null;
		if (!price) return null;

		price -= 5 * (mainLevel - 1);
		if (price <= 0) return null;

		return price;
	}

	public get recommendedUpgrades(): number[] | null {
		return this.config.recommendedUpgrades ?? null;
	}

	public getParamByName(
		name: Partial<keyof TileEntityParams>,
	): number | null {
		return this.tilerepo.getParam(this.index, name);
	}

	public setParamByName(name: keyof TileEntityParams, value: number): void {
		this.tilerepo.setParam(this.index, name, value);
	}

	public get canUpgradeTo(): Set<FrameName> {
		if (this.cachedCanUpgradeTo) return this.cachedCanUpgradeTo;

		const upgrades = new Set<FrameName>();
		if (!this.upgrades.size) return (this.cachedCanUpgradeTo = upgrades);

		const neighbours = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				0,
				false,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				const [tileId0, , tileId2] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				const tileId = tileId2 || tileId0;
				if (!tileId) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId);
				if (!frameName) return null;

				return {
					tileIndex,
					tileId,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null &&
					this.tilerepo.getParam(
						neighbour.tileIndex,
						TileEntityParam.VISIBILITY,
					) === TileVisibilityType.EXPLORED,
			) as Array<TileNeighbour>;

		/*
		// ak je jeden neighbour settlement empty, tak mozem stavat
		const isNeighbourSettlement = neighbours.some(
			({ frameName, tileIndex }: TileNeighbour): boolean =>
				tilerepoSettlementEmptyFrameNames.has(frameName) ||
				tilerepoSettlementEmptyFrameNames.has(
					this.tilerepo.getFrameNameByTileId(
						this.tilerepo.getTileIdsByTileIndex(tileIndex)[0],
					) as FrameName,
				),
		);

		// ak je jeden neighbour cesta, ktora je connectnuta k hlavnemu settlementu, mozem stavat
		const isNeighbourRoad = neighbours.some(
			({ frameName, tileIndex }: TileNeighbour): boolean =>
				isRoad(frameName) &&
				this.tilerepo.getParam(
					tileIndex,
					TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
				) === 1,
		);

		// ak je jeden settlement building, tak mozem stavat
		const isNeighbourBuilding = neighbours.some(
			({ frameName, tileIndex }: TileNeighbour): boolean =>
				isBuilding(frameName) &&
				this.tilerepo.getParam(
					tileIndex,
					TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
				) === 1,
		);
		*/

		const canBuildRoad = neighbours.some(
			({ frameName, tileIndex }: TileNeighbour): boolean =>
				(isRoad(frameName) ||
					isBuilding(frameName) ||
					tilerepoSettlementEmptyFrameNames.has(frameName)) &&
				this.tilerepo.getParam(
					tileIndex,
					TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
				) === 1,
		);

		const sortedFrameNamesByUnlockLevel = Array.from(
			this.upgrades.keys(),
		).sort((aFrameName: FrameName, bFrameName: FrameName): number => {
			const aConfig = this.tilerepo.createEntityByFrameName(aFrameName);
			const bConfig = this.tilerepo.createEntityByFrameName(bFrameName);
			if (!aConfig || !bConfig) return 0;

			const aLevel = aConfig.requirements.get(ResourceType.LEVEL) ?? 0;
			const bLevel = bConfig.requirements.get(ResourceType.LEVEL) ?? 0;
			return aLevel - bLevel;
		});

		for (const frameName of sortedFrameNamesByUnlockLevel) {
			const entity = this.tilerepo.createEntityByFrameName(frameName);
			if (!entity) continue;

			// Hide stored temporary event buildings from tile options when their event is inactive
			if (
				tilerepoRemovableEventBuildingsFrameNames.has(frameName) &&
				!isTemporaryEventBuildingActive(frameName)
			)
				continue;

			if (
				!this.isLevelRequirementSatisfied(
					entity,
					// true,
				)
			)
				continue;

			const { requirements } = entity;
			if (
				requirements.has(ResourceType.LEVEL) &&
				[
					OFFER_BUILDING_UNLOCK_LEVEL,
					EVENT_BUILDING_UNLOCK_LEVEL,
				].includes(requirements.get(ResourceType.LEVEL)!) &&
				tilerepoNonSellableFrameNames.has(frameName)
			) {
				const buildingsStorageStore = useBuildingsStorageStore();
				if (tilerepoLightHousesFrameNames.has(frameName)) {
					if (
						buildingsStorageStore[FrameName.LIGHTHOUSE] +
							buildingsStorageStore[
								FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE
							] <=
						0
					) {
						continue;
					}
				} else if (tilerepoBlastFurnacesFrameNames.has(frameName)) {
					if (
						buildingsStorageStore[FrameName.HILLS_BLAST_FURNACE] +
							buildingsStorageStore[
								FrameName.MOUNTAINS_BLAST_FURNACE
							] <=
						0
					) {
						continue;
					}
				} else if (tilerepoLookoutTowersFrameNames.has(frameName)) {
					if (
						buildingsStorageStore[FrameName.HILLS_LOOKOUT_TOWER] +
							buildingsStorageStore[
								FrameName.MOUNTAINS_LOOKOUT_TOWER
							] <=
						0
					) {
						continue;
					}
				} else if (
					useEventStore().isActive &&
					tilerepoPurchaseableEventBuildings.has(frameName)
				) {
					const isStorageEmpty =
						buildingsStorageStore[frameName] <= 0;
					const { type } = useEventStore().attributes;
					if (
						isStorageEmpty &&
						tilerepoPurchasableBuildingsTupleArrays.every(
							([eventCategory, eventFrameName]) =>
								frameName !== eventFrameName ||
								type !== eventCategory,
						)
					) {
						continue;
					}
				} else if (buildingsStorageStore[frameName] <= 0) {
					continue;
				}
			}

			/*
			// ak budova nepotrebuje cestu, tak mozem stavat
			const isRoadlessBuilding = requirements.has(
				ResourceType.SETTLEMENT_CONNECTION,
			)
				? requirements.get(ResourceType.SETTLEMENT_CONNECTION)! === 0
				: false;

			if (
				!isNeighbourSettlement &&
				!isNeighbourRoad &&
				!isNeighbourBuilding &&
				!isRoadlessBuilding
			)
				continue;
			*/

			if (tilerepoRoadsFrameNames.has(entity.frameName) && !canBuildRoad)
				continue;
			if (this.isBlockedByEnemy && this.isBlockedBy === "enemies") {
				const ssr = useSiegeSystemRepository();
				if (!ssr.isFriendlySiegeSystemUnitByFrameName(entity.frameName))
					continue;
			}

			upgrades.add(frameName);
		}

		return (this.cachedCanUpgradeTo = upgrades);
	}

	public isUpgradeToDisabled(frameName: FrameName): boolean {
		if (!this.upgrades.has(frameName)) return true;
		if (
			!tilerepoFriendlyUnitsFrameNames.has(frameName) &&
			this.isCuttable &&
			this.isDemolishDisabled
		)
			return true;

		if (
			tilerepoNonSellableFrameNames.has(frameName) ||
			useUpgradesStore().getCurrentLevelById(UpgradeIds.inventory) > 0
		) {
			const buildingsStorageStore = useBuildingsStorageStore();
			let entityInStorageCount = 0;
			if (tilerepoFishTrapsFrameNames.has(frameName)) {
				tilerepoFishTrapsFrameNames.forEach(
					(frameName: FrameName): void => {
						entityInStorageCount +=
							buildingsStorageStore?.[frameName] ?? 0;
					},
				);
			} else if (tilerepoFishingBoatsFrameNames.has(frameName)) {
				tilerepoFishingBoatsFrameNames.forEach(
					(frameName: FrameName): void => {
						entityInStorageCount +=
							buildingsStorageStore?.[frameName] ?? 0;
					},
				);
			} else {
				entityInStorageCount = buildingsStorageStore?.[frameName] ?? 0;
			}
			if (tilerepoLightHousesFrameNames.has(frameName)) {
				if (
					buildingsStorageStore[FrameName.LIGHTHOUSE] +
						buildingsStorageStore[
							FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE
						] !==
					0
				) {
					return false;
				}
			} else if (tilerepoBlastFurnacesFrameNames.has(frameName)) {
				if (
					buildingsStorageStore[FrameName.HILLS_BLAST_FURNACE] +
						buildingsStorageStore[
							FrameName.MOUNTAINS_BLAST_FURNACE
						] !==
					0
				) {
					return false;
				}
			} else if (tilerepoLookoutTowersFrameNames.has(frameName)) {
				if (
					buildingsStorageStore[FrameName.HILLS_LOOKOUT_TOWER] +
						buildingsStorageStore[
							FrameName.MOUNTAINS_LOOKOUT_TOWER
						] !==
					0
				) {
					return false;
				}
			} else if (entityInStorageCount > 0) {
				const { requirements } = getBuildingConfig(frameName);
				if (
					requirements.has(ResourceType.SETTLEMENT_LEVEL) &&
					this.assignedCastleTileEntity &&
					this.assignedCastleTileEntity.level <
						requirements.get(ResourceType.SETTLEMENT_LEVEL)!
				)
					return true;
				return false;
			}
		}

		const entity =
			frameName !== this.frameName
				? this.tilerepo.createEntityByFrameName(frameName)
				: this;
		if (!entity) return true;

		if (this.isBlockedByEnemy) {
			const ssr = useSiegeSystemRepository();
			if (
				!ssr.isUnlocked ||
				!ssr.isFriendlySiegeSystemUnitByFrameName(frameName) ||
				!ssr.canBuildFriendlyUnitByTileIndex(this.index, frameName)
			)
				return true;
		}

		if (
			!this.isLevelRequirementSatisfied(
				entity,
				// true,
			)
		)
			return true;

		if (
			entity.requirements.has(ResourceType.SETTLEMENT_LEVEL) &&
			this.assignedCastleTileEntity &&
			this.assignedCastleTileEntity.level <
				entity.requirements.get(ResourceType.SETTLEMENT_LEVEL)!
		)
			return true;

		const { validateResourceAmount } = useResourcesStore();
		const { consumption } = entity;
		if (!consumption) return true;
		if (
			consumption.has(ResourceType.FOOD_CONSUMPTION) &&
			!validateResourceAmount(
				ResourceType.FOOD_DIFF,
				consumption.get(ResourceType.FOOD_CONSUMPTION)! -
					(this.consumption.has(ResourceType.FOOD_CONSUMPTION)
						? this.consumption.get(ResourceType.FOOD_CONSUMPTION)!
						: 0),
			)
		)
			return true;

		if (
			consumption.has(ResourceType.POPULATION_CONSUMPTION) &&
			!validateResourceAmount(
				ResourceType.POPULATION_DIFF,
				consumption.get(ResourceType.POPULATION_CONSUMPTION)! -
					(this.consumption.has(ResourceType.POPULATION_CONSUMPTION)
						? this.consumption.get(
								ResourceType.POPULATION_CONSUMPTION,
							)!
						: 0),
			)
		)
			return true;

		if (useBuildingsStorageStore()[frameName] > 0) return false;

		const price = this.resolveDynamicResources(
			this.upgrades.get(frameName)!.price ?? {},
		);
		if (!price) return true;

		const entries = Object.entries(price || {}) as Array<
			[ResourceType, number]
		>;
		if (entries.length) {
			return entries.some(
				([key, value]: [ResourceType, number]): boolean =>
					!validateResourceAmount(key, value),
			);
		}

		return false;
	}

	public get isDemolishDisabled(): boolean {
		if (tilerepoFriendlyUnitsFrameNames.has(this.frameName)) return false;
		if (this.isBlockedByEnemy) return true;
		if (tilerepoPremiumBuildings.has(this.frameName)) return false;
		if (this.isCuttable)
			return useSettlementsStore().mainLevel < CUTTABLE_LEVEL;
		if (
			!isBuilding(this.frameName) &&
			!this.isLevelRequirementSatisfied(
				this,
				// false,
			)
		)
			return true;
		return false;
	}

	public get isStorable(): boolean {
		return (
			tilerepoPremiumBuildings.has(this.frameName) ||
			tilerepoNonSellableFrameNames.has(this.frameName) ||
			(useUpgradesStore().getCurrentLevelById(UpgradeIds.inventory) > 0 &&
				tilerepoBuildingsFrameNames.has(this.frameName) &&
				!tilerepoNotStorableFrameNames.has(this.frameName) &&
				!tilerepoSettlementCastleBuildingsFrameNames.has(
					this.frameName,
				) &&
				!tilerepoRoadsFrameNames.has(this.frameName))
		);
	}

	public isLevelRequirementSatisfied(
		entity: TileEntity,
		// checkEarlyAccessStorage: boolean,
	): boolean {
		if (!entity) return false;
		const { mainLevel } = useSettlementsStore();
		const { validateBuildingsStorageAmount } = useBuildingsStorageStore();
		if (
			validateBuildingsStorageAmount(entity.frameName, {
				ignoreBoatLimit: true,
			})
		)
			return true;
		if (!entity.requirements.has(ResourceType.LEVEL)) return true;

		const isMainLevelSatisfied =
			entity.requirements.has(ResourceType.LEVEL) &&
			entity.requirements.get(ResourceType.LEVEL)! <= mainLevel;
		// const isEarlyAccessLevelSatisfied =
		// 	entity.requirements.has(ResourceType.EARLY_ACCESS_LEVEL) &&
		// 	entity.requirements.get(ResourceType.EARLY_ACCESS_LEVEL)! <=
		// 		mainLevel &&
		// 	(!checkEarlyAccessStorage ||
		// 		buildingsStorageStore[entity.frameName] > 0);
		const isLevelBypassBuilding = [
			OFFER_BUILDING_UNLOCK_LEVEL,
			EVENT_BUILDING_UNLOCK_LEVEL,
		].includes(
			entity.requirements.has(ResourceType.LEVEL)
				? entity.requirements.get(ResourceType.LEVEL)!
				: 0,
		);

		return (
			isMainLevelSatisfied ||
			// isEarlyAccessLevelSatisfied ||
			isLevelBypassBuilding
		);
	}

	/**
	 * Vrati validnych susedov podla range a polohy tejto konkretnej entity.
	 */
	public getOwnNeighbourTileEntities(): Array<TileEntity> {
		if (!this.range) throw new Error("Range is not defined!");
		return this.getOwnNeighbourTileEntitiesByNeighbourFrameName();
	}

	/**
	 * Vrati validnych susedov podla range poskytnutej budovy a podla polohy tento zakladnej entity, z ktorej sa vola tato funkcia.
	 */
	public getOwnNeighbourTileEntitiesByNeighbourFrameName(
		frameName: FrameName | null = null,
		limit: number = Infinity,
	): Array<TileEntity> {
		let { range } = this;

		if (frameName) {
			const entity = this.tilerepo.createEntityByFrameName(frameName, {
				x: SAFE_COORDINATE_OUTSIDE_MAP,
				y: SAFE_COORDINATE_OUTSIDE_MAP,
			});
			if (!entity?.range) throw new Error("Range is not defined!");
			range = entity.range;
		}

		if (!range) throw new Error("Range is not defined!");

		return (
			this.tilerepo
				.getNeighbourTileIndexesByTileCoordinates(
					{ x: this.x, y: this.y },
					range || 0,
				)
				.map((tileIndex: TileIndex): TileEntity | null => {
					const _tileId =
						this.tilerepo.getTileIdByTileIndex(tileIndex);
					if (!_tileId) return null;
					const _frameName =
						this.tilerepo.getFrameNameByTileId(_tileId);
					if (!_frameName) return null;
					if (frameName && _frameName !== frameName) return null;

					let supplies: Set<FrameName> | undefined;
					switch (true) {
						case isTerrain(_frameName):
							supplies = this.resolveSupplies(
								getTerrainConfig(_frameName)?.supplies ??
									new Set<FrameName>(),
							);
							break;
						case isBuilding(_frameName):
							supplies = this.resolveSupplies(
								getBuildingConfig(_frameName)?.supplies ??
									new Set<FrameName>(),
							);
							break;
						case isPoi(_frameName):
							supplies = this.resolveSupplies(
								getPoiConfig(_frameName)?.supplies ??
									new Set<FrameName>(),
							);
							break;
					}
					if (!supplies || !supplies.has(this.frameName)) return null;

					const entity =
						this.tilerepo.getTileEntityByTileIndex(tileIndex);
					if (!entity) return null;
					if (
						isPoi(entity.frameName)
							? !(entity as AbstractPoiTileEntity).isFullyExplored
							: !entity.isExplored
					)
						return null;
					if (entity.isBlockedByEnemy) return null;
					if (!entity.isConnectionToMainSettlementActive) return null;

					// duplicitna logika
					// if (!entity.supplies.has(this.frameName)) return null;
					if (!entity.suppliesTileIndexes.has(this.index))
						return null;
					return entity;
				})
				.filter(
					(entity: TileEntity | null): boolean => entity !== null,
				) as Array<TileEntity>
		).slice(0, limit);
	}

	public getOwnNeighbourTerrainTileEntities(
		mode: "range" | "self" = "range",
		limit: number = Infinity,
	): Array<TileEntity> {
		switch (mode) {
			case "self": {
				const [tileId0] = this.tilerepo.getTileIdsByTileIndex(
					this.index,
				)!;
				const frameName0 = this.tilerepo.getFrameNameByTileId(tileId0)!;
				const entity0 =
					this.tilerepo.createEntityByFrameName(frameName0)!;
				if (!entity0?.supplies?.has(this.frameName)) return [];
				return [entity0];
			}
			case "range": {
				if (!this.range) throw new Error("Range is not defined!");
				return this.tilerepo
					.getNeighbourTileIndexesByTileCoordinates(
						{
							x: this.x,
							y: this.y,
						},
						this.range,
						true, // ryby / lesy sa tazia aj spod seba
					)
					.map((tileIndex: TileIndex): TileEntity | null => {
						const [tileId0] =
							this.tilerepo.getTileIdsByTileIndex(tileIndex)!;
						const frameName0 =
							this.tilerepo.getFrameNameByTileId(tileId0)!;
						const entity0 =
							this.tilerepo.createEntityByFrameName(frameName0)!;
						if (!entity0?.supplies?.has(this.frameName))
							return null;
						return entity0;
					})
					.filter(
						(entity: TileEntity | null): boolean => entity !== null,
					)
					.slice(0, limit) as Array<TileEntity>;
			}
			default: {
				return [];
			}
		}
	}

	private resolveSupplies(supplies: Set<SuppliesValue>): Set<FrameName> {
		const resolved = new Set<FrameName>();

		for (const frameNameOrCallback of supplies) {
			if (typeof frameNameOrCallback !== "function") {
				resolved.add(frameNameOrCallback);
				continue;
			}

			const resolvedCallback = frameNameOrCallback();
			if (!resolvedCallback) continue;

			if (typeof resolvedCallback === "string") {
				resolved.add(resolvedCallback);
				continue;
			}

			for (const frameName of resolvedCallback) {
				resolved.add(frameName);
			}
		}

		return resolved;
	}

	public get sameBuildingCount(): number {
		return this.tilerepo.getTileCount2(
			new Set<FrameName>([this.frameName]),
		);
	}

	public get progress(): number | null {
		const ssr = useSiegeSystemRepository();
		if (!ssr.isActive) return null;

		if (
			!tilerepoFriendlyUnitsFrameNames.has(this.frameName) &&
			!tilerepoFightableEnemiesFrameNames.has(this.frameName)
		)
			return null;

		const maxHp = this.siegeAttributes?.maxHp ?? null;
		if (!maxHp) return null;

		const currentHp = ssr.getCurrentHpByTileIndex(this.index);
		if (currentHp === null) return null;

		return currentHp / maxHp;
	}

	public get friendlyUnitDisbandReward(): Partial<Reward> | null {
		if (!tilerepoFriendlyUnitsFrameNames.has(this.frameName)) return null;

		const { reward: baseReward = {} } =
			this.upgrades.get(FrameName.NOT_SET) || {};
		const ssr = useSiegeSystemRepository();
		const currentHp = ssr.getCurrentHpByTileIndex(this.index);
		const maxHp = ssr.getMaxHpByTileIndex(this.index);
		if (!maxHp) return null;

		const rewardMultiplier = (Math.max(currentHp ?? 0, 0) / maxHp) * 0.5;
		const entries = Object.entries(baseReward || {}) as Array<
			[ResourceType, UpgradeRewardValue]
		>;
		if (!entries.length) return null;

		return entries.reduce(
			(acc: Reward, [key, value]: [ResourceType, UpgradeRewardValue]) => {
				const resolvedValue =
					typeof value === "function" ? value() : value;
				acc[key] = floorNumber(resolvedValue * rewardMultiplier);
				return acc;
			},
			{} as Reward,
		);
	}

	public resolveDynamicResources(
		resources: Partial<Record<ResourceType, ResourceValue>>,
	): Partial<Record<ResourceType, number>> {
		return Object.fromEntries(
			(
				Object.entries(resources) as Array<
					[ResourceType, ResourceValue]
				>
			).map(
				([key, value]: [ResourceType, ResourceValue]): [
					ResourceType,
					number,
				] => [key, typeof value === "function" ? value(this) : value],
			),
		);
	}
}

export default TileEntity;
